import { DestroyRef, Injectable, OnDestroy, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { NavigationItem } from './navigation-item.interface';
import { BehaviorSubject, Observable, Subscription, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@core/services/auth/auth.store';
import { User } from '../../shared/definitions/user';
import { PropertyStore } from '../../shared/services/property.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { DocumentService } from '@core/services/data/document.service';
import { NavigationBadgeService } from '@core/services/navigation/navigation-badge.service';
import { WorkspaceService } from '@core/services/auth/workspace.service';
import { WorkspaceStore } from '../../shared/services/workspace.store';
import { WorkspaceType, ownerLikeWorkspaceType } from '../../shared/definitions/workspace';
import { AGENCY_REACH } from './agency-reach.token';
import { AiAssistantService } from '@core/services/ai/ai-assistant.service';
import { WORKSPACE_SETUP_PROGRESS } from './workspace-setup-progress.token';
import { countBadge, rentArrearsBadge, setupProgressBadge } from './sidenav-badges.util';
import { navIcon } from '../icons/nav-icons';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService implements OnDestroy {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);
  private hasProperties = false;
  private hasDocuments = false;
  private subscriptions: Subscription[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(VexLayoutService);
  private readonly authStore = inject(AuthStore);
  private readonly propertyStore = inject(PropertyStore);
  private readonly documentService = inject(DocumentService);
  private readonly navigationBadgeService = inject(NavigationBadgeService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly translate = inject(TranslateService);
  private readonly agencyOrg = inject(AGENCY_REACH);
  private readonly assistant = inject(AiAssistantService);
  private readonly setupProgress = inject(WORKSPACE_SETUP_PROGRESS);

  // Cached signal-to-observable bridge. Must be created in injection context
  // (class field init runs during construction); using `toObservable` inside
  // `loadUserData` after `await` throws NG0203.
  private readonly propertyList$ = toObservable(
    computed(() => [...this.propertyStore.list()])
  );

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor() {
    this.initEffects();
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Reactive triggers driven by signals. Re-runs whenever the authenticated
   * user or workspace changes (login / logout / claims refresh / switch).
   */
  private initEffects(): void {
    effect(() => {
      const user = this.authStore.user();
      if (user && user.userUID) {
        void this.loadUserData(user);
      } else {
        this._items.next([]);
      }
    });

    effect(() => {
      const workspace = this.workspaceStore.currentWorkspace();
      console.log('🔄 NavigationLoader: Workspace changed to:', workspace?.type || 'none');
      const currentUser = this.authStore.user();
      if (currentUser && currentUser.userUID) {
        this.updateNavigation(currentUser);
      }
    });

    // Agency menu adapts to the member's BUSINESS role (délégué vs manager vs
    // principal). The role resolves asynchronously (agency_team_members loads
    // after the workspace), so re-emit the menu when reach changes.
    effect(() => {
      // Touch the reach signals so this effect re-runs when the role resolves.
      this.agencyOrg.fullReach();
      this.agencyOrg.teamReach();
      const currentUser = this.authStore.user();
      const ws = this.workspaceStore.currentWorkspace();
      if (currentUser && currentUser.userUID && ws?.type === 'agency') {
        this.updateNavigation(currentUser);
      }
    });

    // The « Mise en route » percentage is a badge on the PM nav. Its inputs
    // (properties, tenants, contracts…) settle asynchronously, so re-emit the
    // menu when the figure moves — same shape as the agency-reach effect.
    effect(() => {
      this.setupProgress.percentage();
      this.setupProgress.loading();
      const currentUser = this.authStore.user();
      const ws = this.workspaceStore.currentWorkspace();
      if (currentUser && currentUser.userUID && ownerLikeWorkspaceType((ws?.type || 'personal') as WorkspaceType)) {
        this.updateNavigation(currentUser);
      }
    });
  }

  /**
   * Non-signal sources: badge counts are still observable-driven, so we
   * keep an RxJS subscription and read `authStore.user()` synchronously
   * to decide whether to refresh navigation.
   */
  private initSubscriptions(): void {
    const badgeSub = this.navigationBadgeService.badgeCounts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentUser = this.authStore.user();
      if (currentUser && currentUser.userUID) {
        this.updateNavigation(currentUser);
      }
    });
    this.subscriptions.push(badgeSub);

    // Re-emit navigation on every language change. The nav labels are i18n
    // KEYS resolved per-item, and the persistent OnPush sidenav-item leaves
    // (projected deep inside mat-sidenav-container, behind clean OnPush
    // ancestors) are NOT reliably refreshed when they mark themselves dirty
    // from a translate event. Re-emitting items$ drives change detection
    // through SidenavComponent's `items$ | async` (the same proven path that
    // already refreshes the sidenav on badge/workspace changes), so the whole
    // sidenav re-renders with the new language without an F5.
    const langSub = merge(
      this.translate.onLangChange,
      this.translate.onTranslationChange
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentUser = this.authStore.user();
      if (currentUser && currentUser.userUID) {
        this.updateNavigation(currentUser);
      }
    });
    this.subscriptions.push(langSub);
  }

  private async loadUserData(user: User): Promise<void> {
    try {
      console.log('🔄 NavigationLoader: Waiting for workspace...');
      await this.workspaceStore.waitForWorkspace(10000);

      const propertySub = this.propertyList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(properties => {
        this.hasProperties = properties.length > 0;
        this.updateNavigation(user);
      });
      this.subscriptions.push(propertySub);

      const documentSub = this.documentService.documentsExist$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(hasDocuments => {
        this.hasDocuments = hasDocuments;
        this.updateNavigation(user);
      });
      this.subscriptions.push(documentSub);
    } catch (error) {
      console.error('❌ NavigationLoader: Failed to load navigation data', error);
      this.updateNavigation(user);
    }
  }

  private updateNavigation(user: User): void {
    const workspace = this.workspaceStore.currentWorkspace();
    const workspaceType = (workspace?.type || 'personal') as WorkspaceType;

    if (workspaceType === 'service_provider') {
      this.buildServiceProviderNavigation();
      return;
    }

    if (workspaceType === 'agency') {
      this.buildAgencyNavigation();
      return;
    }

    if (workspaceType === 'syndic') {
      this.buildSyndicNavigation();
      return;
    }

    this.buildPropertyManagerNavigation(user);
  }

  /**
   * Sidenav entry that opens the AI assistant panel (no route). Only emitted
   * when the assistant is available for the current workspace type.
   */
  private assistantNavItem(): NavigationItem | null {
    if (!this.assistant.isAvailable()) return null;
    const isMac =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad/.test(navigator.platform);
    return {
      type: 'link',
      label: 'shared.nav.virtualAssistant',
      icon: navIcon('spark'),
      keyboardShortcut: isMac
        ? 'shared.nav.virtualAssistantShortcutMac'
        : 'shared.nav.virtualAssistantShortcut',
      // No route, so no routerLinkActive: the panel's own open state is the
      // active state.
      isActive: () => this.assistant.isOpen(),
      route: () => {
        this.assistant.toggle();
        // Function-route items don't fire NavigationEnd. Dismiss the overlay
        // drawer on small screens only — on ≥960px the sidenav is persistent
        // (mode `side`); closing it would make the aside disappear.
        if (this.layoutService.isLtMd()) {
          this.layoutService.closeSidenav();
        }
      }
    };
  }

  private buildSyndicNavigation(): void {
    const assistant = this.assistantNavItem();
    const navigationItems: NavigationItem[] = [
      // The cockpit is workspace-agnostic; a syndic workspace already accumulates
      // its own action cards (AG / maintenance plans / legal deadlines / loans).
      {
        type: 'link',
        label: 'shared.nav.cockpit',
        route: '/',
        icon: navIcon('cockpit'),
        routerLinkActiveOptions: { exact: true }
      },
      ...(assistant ? [assistant] : []),
      {
        type: 'link',
        label: 'shared.nav.buildings',
        route: '/syndic/dashboard',
        icon: navIcon('building'),
        routerLinkActiveOptions: { exact: true }
      },
      // Owners asking to link their account to a lot (handoff: "Demandes").
      {
        type: 'link',
        label: 'shared.nav.connectionRequests',
        route: '/syndic/connection-requests',
        icon: navIcon('link'),
        routerLinkActiveOptions: { exact: true }
      },
      { type: 'subheading', label: '', children: [] },
      // Operational workflows — syndics raise the same interventions and repairs
      // and rely on the same provider carnet as property managers, scoped to their
      // buildings.
      {
        type: 'link',
        label: 'shared.nav.interventions',
        route: '/interventions',
        icon: navIcon('toolbox')
      },
      {
        type: 'link',
        label: 'shared.nav.repairs',
        route: '/repairs/list',
        icon: navIcon('wrench')
      },
      {
        type: 'link',
        label: 'shared.nav.myServiceProviders',
        route: '/service-providers',
        icon: navIcon('provider')
      },
      { type: 'subheading', label: '', children: [] },
      {
        type: 'link',
        label: 'shared.nav.newBuilding',
        route: '/syndic/buildings/new',
        icon: navIcon('building-plus')
      }
    ];

    this._items.next(navigationItems);
  }

  private buildAgencyNavigation(): void {
    // Business reach drives WHAT each role sees — keeping the menu focused
    // (a délégué doesn't see settings/connectors, a manager gets team pilotage).
    const fullReach = this.agencyOrg.fullReach();   // owner/admin/principal
    const teamReach = this.agencyOrg.teamReach();   // business manager
    const assistant = this.assistantNavItem();

    const navigationItems: NavigationItem[] = [
      {
        type: 'link',
        label: 'shared.nav.dashboard',
        route: '/agency/dashboard',
        icon: navIcon('dashboard'),
        routerLinkActiveOptions: { exact: true }
      },
      ...(assistant ? [assistant] : []),
      {
        type: 'link',
        label: 'shared.nav.myDay',
        route: '/agency/my-day',
        icon: navIcon('sunrise')
      },
      { type: 'subheading', label: '', children: [] },
      {
        type: 'link',
        label: 'shared.nav.properties',
        route: '/agency/properties',
        icon: navIcon('house')
      },
      {
        type: 'link',
        label: 'shared.nav.leads',
        route: '/agency/leads',
        icon: navIcon('lead')
      },
      {
        type: 'link',
        label: 'shared.nav.estimations',
        route: '/agency/estimations',
        icon: navIcon('valuation')
      },
      {
        type: 'link',
        label: 'shared.nav.viewings',
        route: '/agency/viewings',
        icon: navIcon('viewing')
      },
      {
        type: 'link',
        label: 'shared.nav.mandatesInbox',
        route: '/agency/mandates',
        icon: navIcon('mandate')
      },
      {
        type: 'link',
        label: 'agency.deals.title',
        route: '/agency/deals',
        icon: navIcon('deal')
      }
    ];

    // Pilotage — managers + principals/admins.
    if (fullReach || teamReach) {
      navigationItems.push({ type: 'subheading', label: '', children: [] });
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.teamPerformance',
        route: '/agency/team-performance',
        icon: navIcon('podium')
      });
    }

    // Administration — full reach only (owner/admin/principal).
    if (fullReach) {
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.connectors',
        route: '/agency/connectors',
        icon: navIcon('plug')
      });
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.agencyWebsite',
        route: '/agency/website',
        icon: navIcon('website')
      });
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.agencySettings',
        route: '/agency/settings',
        icon: navIcon('sliders')
      });
    }

    this._items.next(navigationItems);
  }

  private buildServiceProviderNavigation(): void {
    // The sidenav link is the ONLY way into the assistant: the panel component
    // hosts the panel but renders no trigger of its own. Opening
    // `CHAT_ENABLED_WORKSPACE_TYPES` to `service_provider` without adding the
    // item here left the provider with a working catalogue and no door to it.
    const assistant = this.assistantNavItem();

    const navigationItems: NavigationItem[] = [
      {
        type: 'link',
        label: 'shared.nav.dashboard',
        route: '/provider/dashboard',
        icon: navIcon('dashboard'),
        routerLinkActiveOptions: { exact: true }
      },
      ...(assistant ? [assistant] : []),
      {
        type: 'subheading',
        label: '',
        children: []
      },
      {
        type: 'link',
        label: 'shared.nav.jobRequests',
        route: '/provider/jobs',
        icon: navIcon('inbox')
      },
      {
        type: 'subheading',
        label: '',
        children: []
      },
      // #525 — the notifications settings screen (audience-aware, shows the
      // provider its own rows) was only reachable via the manager-oriented
      // user-menu dropdown, unannounced anywhere in this sidenav.
      {
        type: 'link',
        label: 'shared.nav.notifications',
        // Les réglages, pas la liste : c'est l'écran que #525 voulait rendre
        // atteignable. La liste vit sur `/apps/notifications`, sous la cloche.
        route: '/apps/user-profile',
        queryParams: { tab: 'notifications' },
        icon: navIcon('bell')
      },
      {
        type: 'link',
        label: 'shared.nav.publicProfile',
        route: '/provider/profile',
        icon: navIcon('profile')
      }
    ];

    this._items.next(navigationItems);
  }

  private buildPropertyManagerNavigation(_user: User): void {
    const badgeCounts = this.navigationBadgeService.getCurrentCounts();
    const assistant = this.assistantNavItem();

    // The percentage badge needs the same Firestore listeners the page uses;
    // without them the figure would claim « no tenant » on a workspace that
    // has some. Idempotent — the page and the cockpit band call it too. Only
    // once a workspace is resolved: the badge subject emits synchronously at
    // construction, and activating then makes InventoryReportService fetch
    // against « No workspace loaded ».
    if (this.workspaceStore.currentWorkspace()) {
      this.setupProgress.activate();
    }

    const navigationItems: NavigationItem[] = [
      {
        // Hub de complétude de l'espace de travail. Placé AU-DESSUS du Cockpit :
        // c'est la première chose à faire, et elle doit se voir en premier.
        type: 'link',
        label: 'shared.nav.gettingStarted',
        route: '/getting-started',
        icon: navIcon('rocket'),
        // No badge while the figure cannot be computed honestly (DESIGN.md §13.3).
        badge: this.setupProgress.loading()
          ? undefined
          : setupProgressBadge(this.setupProgress.percentage())
      },
      {
        type: 'link',
        label: 'shared.nav.cockpit',
        route: '/',
        icon: navIcon('cockpit'),
        routerLinkActiveOptions: { exact: true },
        // Cards waiting on the owner — amber like the inbox, it is a queue.
        badge: countBadge(badgeCounts.cockpit, 'bg-amber-500')
      },
      ...(assistant ? [assistant] : []),
      {
        // One entry for every communication (#563). The page itself is the
        // hub: channel tabs (e-mail / SMS / WhatsApp / other) over Received ·
        // Scheduled · Sent, so the sidenav no longer needs an accordion to
        // reach the queue of not-yet-sent mail or the outbox.
        type: 'link',
        label: 'shared.nav.inbox',
        route: '/apps/communications/inbox',
        icon: navIcon('inbox'),
        // Drafts waiting for approval. Amber, not green: unlike the
        // interventions count this is a queue waiting on the USER.
        badge: badgeCounts.communications > 0 ? {
          value: badgeCounts.communications.toString(),
          bgClass: 'bg-amber-500',
          textClass: 'text-white'
        } : undefined
      }
    ];

    if (this.hasProperties) {
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.interventions',
        route: '/interventions',
        icon: navIcon('toolbox'),
        badge: badgeCounts.maintenance > 0 ? {
          value: badgeCounts.maintenance.toString(),
          bgClass: 'bg-green-600',
          textClass: 'text-white'
        } : undefined
      });
    }

    navigationItems.push({
      type: 'link',
      label: 'shared.nav.financialOverview',
      route: '/financial-dashboard',
      icon: navIcon('finance'),
      routerLinkActiveOptions: { exact: true }
    });

    // Juste sous la vue financiere, parce que c'est la meme question posee
    // autrement: celle-la dit combien on gagne, celle-ci dit ce qui manque.
    // Un ecran dedie qu'on ne peut atteindre qu'en tapant son URL n'est pas un
    // ecran (#396).
    navigationItems.push({
      type: 'link',
      label: 'shared.nav.rentArrears',
      route: '/rent-arrears',
      icon: navIcon('rent-arrears'),
      routerLinkActiveOptions: { exact: true },
      // Nothing when nothing is late; orange within the 5-day grace, red past it.
      badge: rentArrearsBadge(badgeCounts.rentArrears, badgeCounts.rentArrearsMaxDays)
    });

    navigationItems.push({ type: 'subheading', label: '', children: [] });

    navigationItems.push({
      type: 'link',
      label: 'shared.nav.properties',
      route: '/properties/list',
      icon: navIcon('house')
    });
    navigationItems.push({
      type: 'link',
      label: 'shared.nav.tenants',
      route: '/tenants/list',
      icon: navIcon('tenant')
    });
    navigationItems.push({
      type: 'link',
      label: 'shared.nav.rentalContracts',
      route: '/contracts/list',
      icon: navIcon('lease')
    });
    // Calculators grouped under « Outils ». The loan entry targets `/loans`
    // (redirects to the list) so the prefix match keeps « Outils » active on
    // /loans/add as well. « Prêts » has no top-level entry of its own any more:
    // it opened that same list, and two doors to one screen said nothing extra.
    navigationItems.push({
      type: 'dropdown',
      label: 'shared.nav.tools',
      icon: navIcon('calculator'),
      children: [
        {
          type: 'link',
          label: 'shared.nav.loanCalculator',
          route: '/loans',
          icon: navIcon('loan')
        },
        {
          type: 'link',
          label: 'shared.nav.investmentCalculator',
          route: '/property-evaluation/investment-calculator/list',
          icon: navIcon('investment')
        },
        {
          type: 'link',
          label: 'shared.nav.indexationCalculator',
          route: '/tools/indexation-calculator',
          icon: navIcon('indexation')
        },
        {
          type: 'link',
          label: 'shared.nav.rentSimulator',
          route: '/tools/rent-simulator',
          icon: navIcon('rent')
        }
      ]
    });

    if (this.hasProperties) {
      navigationItems.push({ type: 'subheading', label: '', children: [] });

      // Flat top-level "Mes prestataires" — a one-child dropdown forced an
      // extra tap for no information gain (#217). Marketplace / public list
      // stays off the menu until it has real data (same rationale as before).
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.myServiceProviders',
        route: '/service-providers',
        icon: navIcon('provider')
      });
    }

    navigationItems.push({ type: 'subheading', label: '', children: [] });

    if (this.hasDocuments) {
      navigationItems.push({
        type: 'link',
        label: 'shared.nav.documentLibrary',
        route: '/apps/documents',
        icon: navIcon('folder')
      });
    }

    this._items.next(navigationItems);
  }
}
